import { useState, useEffect, FormEvent, ChangeEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../../components/Card';
import Button from '../../components/Button';
import ErrorBox from '../../components/ErrorBox';
import { validatePasswordSetupTokenAPI, setupClientPasswordAPI } from '../../api/apiHelper';
import { toast } from 'react-toastify';
import axios from 'axios';

interface ClientUserData {
  email: string;
  first_name: string;
  last_name: string;
  client_name: string;
}

interface FormData {
  password: string;
  confirmPassword: string;
  first_name: string;
  last_name: string;
}

interface FormErrors {
  first_name?: string;
  last_name?: string;
  password?: string;
  confirmPassword?: string;
}

export const ClientSetupPassword = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [clientUser, setClientUser] = useState<ClientUserData | null>(null);
  const [formData, setFormData] = useState<FormData>({
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});

  // Validate token on component mount
  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      setLoading(true);
      if (!token) {
        throw new Error('Invalid token');
      }
      const response = await validatePasswordSetupTokenAPI(token);
      const data = response.data as ClientUserData;
      setClientUser(data);
      setFormData(prev => ({
        ...prev,
        first_name: data.first_name || '',
        last_name: data.last_name || '',
      }));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.error || 'Invalid or expired invitation link');
      } else {
        toast.error('Invalid or expired invitation link');
      }
      setTimeout(() => navigate('/'), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setValidating(true);
      if (!token) throw new Error('Invalid token');
      
      await setupClientPasswordAPI(token, {
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
      });

      toast.success('Password set successfully! You can now log in.');
      setTimeout(() => navigate('/auth/sign-in'), 2000);
    } catch (error) {
      let errorMessage = 'Failed to set password';
      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.error || 
                      error.response?.data?.password?.[0] || 
                      'Failed to set password';
      }
      toast.error(errorMessage);
    } finally {
      setValidating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <Card className="p-6 text-center">
          <p className="text-gray-600">Loading...</p>
        </Card>
      </div>
    );
  }

  if (!clientUser) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <Card className="p-6 text-center">
          <p className="text-red-600 font-semibold">Invalid or expired invitation link</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-2 text-gray-900">Welcome to Telerelation Client Portal</h1>
          <p className="text-gray-600 text-sm">Set up your password to activate your account</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email Display (Read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={clientUser.email}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 cursor-not-allowed"
            />
          </div>

          {/* First Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
            <input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.first_name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter first name"
            />
            {errors.first_name && <p className="text-red-500 text-sm mt-1">{errors.first_name}</p>}
          </div>

          {/* Last Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
            <input
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.last_name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter last name"
            />
            {errors.last_name && <p className="text-red-500 text-sm mt-1">{errors.last_name}</p>}
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.password ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter password (min 8 characters)"
            />
            {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Confirm password"
            />
            {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={validating}
            varient="primary"
            className="w-full mt-6"
          >
            {validating ? 'Setting up...' : 'Set Up Password & Activate'}
          </Button>
        </form>

        <p className="text-center text-gray-500 text-sm mt-4">
          Already have an account? <a href="/auth/sign-in" className="text-blue-600 hover:underline">Sign In</a>
        </p>
      </Card>
    </div>
  );
};

export default ClientSetupPassword;
